// Membresías del club — capa de API. El wrapper HTTP (manejo del 1002, motivo de negocio
// anidado en data.message) vive en `apiClient.js`, compartido: acá quedan sólo los endpoints
// y el vocabulario de UI del módulo.

import { request, post, patch } from './apiClient';

// ── planes ──
export const listPlans = ({ includeInactive = false } = {}) =>
    request(`manage-memberships/plans${includeInactive ? '?all=1' : ''}`);
export const createPlan = (payload) => post('manage-memberships/plans', payload);
export const updatePlan = (id, payload) => patch(`manage-memberships/plans/${id}`, payload);

// ── socios ──
export const listMemberships = ({ status = 'active', q = '' } = {}) => {
    const params = new URLSearchParams({ status });
    if (q) params.set('q', q);
    return request(`manage-memberships?${params.toString()}`);
};
export const sellMembership = (payload) => post('manage-memberships', payload);
export const membershipStatus = (cedula, date) => {
    const digits = String(cedula || '').replace(/\D/g, '');
    const qs = date ? `?date=${encodeURIComponent(date)}` : '';
    return request(`manage-memberships/status/${encodeURIComponent(digits)}${qs}`);
};
const soloDigitos = (cedula) => encodeURIComponent(String(cedula || '').replace(/\D/g, ''));
// La membresía es UNA por socio: el detalle junta sus períodos, pagos y entradas de socio.
export const memberDetail = (cedula) => request(`manage-memberships/member/${soloDigitos(cedula)}`);
// Baja del SOCIO (sólo admin): corta el período en curso y los futuros. Motivo obligatorio.
export const cancelMember = (cedula, reason) => patch(`manage-memberships/member/${soloDigitos(cedula)}/cancel`, { reason });
// Cambio de plan en el momento: la cotización (lo que se cobra hoy) y el cambio. El POST cobra
// exactamente lo cotizado; si cambió en el medio, el backend rechaza con QUOTE_CHANGED.
export const quoteChangePlan = (cedula, planId) =>
    request(`manage-memberships/member/${soloDigitos(cedula)}/change-plan?planId=${encodeURIComponent(planId)}`);
export const changePlan = (cedula, payload) => post(`manage-memberships/member/${soloDigitos(cedula)}/change-plan`, payload);
// Visitas de socios por semana y por plan (y cuánto cover no se cobró por lugares de socio).
export const visitsSummary = ({ from, to } = {}) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const q = params.toString();
    return request(`manage-memberships/visits/summary${q ? `?${q}` : ''}`);
};

// ── vocabulario de UI ──
// Estado de UN PERÍODO (detalle del socio).
export const ESTADO_MEMBRESIA = {
    vigente:   { label: 'Vigente',  badge: 'badge-success' },
    futura:    { label: 'Empieza más adelante', badge: 'badge-info' },
    vencida:   { label: 'Vencida',  badge: 'badge-secondary' },
    cancelada: { label: 'De baja',  badge: 'badge-danger' },
};
// Estado del SOCIO (grilla): el mismo vocabulario; 'baja' = todos sus períodos dados de baja.
export const ESTADO_SOCIO = { ...ESTADO_MEMBRESIA, baja: ESTADO_MEMBRESIA.cancelada };

// Días de la semana en la convención del backend (0 = domingo … 6 = sábado), en el orden en que
// se leen en el local (lunes primero).
export const DIAS_SEMANA = [
    { d: 1, corto: 'L', largo: 'lunes' },
    { d: 2, corto: 'M', largo: 'martes' },
    { d: 3, corto: 'X', largo: 'miércoles' },
    { d: 4, corto: 'J', largo: 'jueves' },
    { d: 5, corto: 'V', largo: 'viernes' },
    { d: 6, corto: 'S', largo: 'sábado' },
    { d: 0, corto: 'D', largo: 'domingo' },
];

// "12345" / [1,2,3,4,5] → [1,2,3,4,5]
export function diasDelPlan(value) {
    if (Array.isArray(value)) return value.map(Number);
    return String(value || '').split('').filter((c) => /[0-6]/.test(c)).map(Number);
}

// Texto corto de los días: "L a V", "todos los días", "S y D", "L, X, V".
export function textoDias(value) {
    const dias = diasDelPlan(value);
    if (!dias.length) return null;
    const set = new Set(dias);
    if (set.size === 7) return 'todos los días';
    if ([1, 2, 3, 4, 5].every((d) => set.has(d)) && set.size === 5) return 'L a V';
    const nombres = DIAS_SEMANA.filter((x) => set.has(x.d)).map((x) => x.corto);
    return nombres.length === 2 ? nombres.join(' y ') : nombres.join(', ');
}

// Beneficios de un plan en una línea: "Club · Mesa sin entrada L a V".
export function textoBeneficios(plan) {
    if (!plan) return '';
    const partes = [];
    if (Number(plan.club_access) !== 0) partes.push('Club');
    const dias = textoDias(plan.free_table_weekdays);
    if (dias) partes.push(`Mesa sin entrada ${dias}`);
    return partes.join(' · ') || 'Sin beneficios';
}

export const METODO_PAGO_MEMBRESIA = {
    efectivo: 'Efectivo',
    transferencia: 'Transferencia',
    pos: 'POS',
    mercadopago: 'Mercado Pago',
    free: 'Cortesía',
};

// Re-export por comodidad de las pantallas de membresías: la implementación vive con el resto
// de los formateadores de fecha (utils/formats.js), que ya tenía la misma regla para fechas
// con hora — tener dos copias es cómo terminan diciendo cosas distintas.
export { formatYMD } from './formats';
